/**
 * テロップの単語ハイライト (カラオケ字幕) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.2-B2。
 *
 * 同じ意味・同じ規則の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出しの注記)
 * - video-editor リポジトリの src/schema/captionWordHighlight.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/caption_extensions.py (AI 提案の正規化・合成フレーム)
 *
 * ## 形 (部分強調 §4.2-I と同じく 2 つに分かれる)
 *
 * - `Caption.wordTimings`: **いつ・どこまで**光らせるか。
 *   `{textHash, units: [{start, end, startSec, endSec}]}`。
 *   - `start` / `end` は `text` 内の**コードポイント**単位の半開区間 (emphasisRanges と同じ単位)
 *   - `startSec` / `endSec` は**シーン表示秒** (Caption の startSec / endSec と同じ基準)
 *   - `textHash` は `text` (NFC) の安定ハッシュ。**描画側は一致しないかぎり無視する**
 *     (宣言を通らない経路 (旧データ・外部からの直接更新) でも誤った位置を光らせないための二重化)
 *   これは**コード側の同期工程が作る導出値**で、AI にも UI にも書かせない (§4.2-B2)
 * - `CaptionDesign.wordHighlight`: **どう**光らせるか (`{color?, mode?, leadSec?}`)。
 *   **キーの存在そのものが「単語ハイライトを使う」宣言**で、中身が空 `{}` でも有効
 *   (省略 = 使わない・`null` = 解除)。AI / UI はこちらだけを書き、`wordTimings` は
 *   同期工程 (caption_alignment.build_caption_word_timings) が付ける
 *
 * ## 描画
 *
 * 現在のシーン表示秒 `t` に対し `wordHighlightRangeAt` が「先頭から k 文字目まで」の 1 区間を返す
 * (発話済み + 発話中の文字)。ハイライトは**色だけ**を変え、部分強調と重なる文字は
 * **強調のサイズ・太さを保ったまま色だけ**ハイライト色にする (mode が `color-bold` のときは
 * ハイライト中の文字も太くする)。位置・不透明度には触らないので入退場アニメーション (§4.2-B1) と
 * 競合せず、タイプライター (同 B1) とは「表示済みの文字数」でクリップして併用できる。
 *
 * 描画の実装は 4 か所 (どれかを直したら 4 つとも直す。部分強調と同じ 4 つ):
 * 1. プレビュー `remotion/EditVideo.tsx` の DesignedCaption (横書き)
 * 2. プレビュー `remotion/VerticalCaption.tsx` (縦書き)
 * 3. MP4 レンダラー (video-editor リポジトリの同名ファイル)
 * 4. 批評・ディレクターが見る合成フレーム `backend/services/video_edit/contact_sheet.py`
 *    (そのタイルの代表時刻でのハイライト状態を描く)
 *
 * **既知の割り切り**: `mode: 'color-bold'` はハイライトが進むにつれ太さが変わるぶん行の幅も動く
 * (フィット縮小の計測は常に基準のウェイトで行う)。既定の `'color'` では起きない。
 *
 * ## 書き出し
 *
 * Premiere の .prtextstyle / FCPXML の title はテロップ単位のスタイルしか持てないので落とし、
 * ZIP の README とトーストで「単語ハイライトは含まれません」と注記する (§0-2)。
 */

import { CAPTION_EMPHASIS_DEFAULT_COLOR, type CaptionEmphasisRange } from './captionEmphasis';

/** ハイライト 1 単位 (whisper の word 1 つぶん)。 */
export interface CaptionWordTimingUnit {
  /** text 内のコードポイント単位の開始位置 (含む)。 */
  start: number;
  /** text 内のコードポイント単位の終了位置 (含まない)。 */
  end: number;
  /** 発話の開始 (シーン表示秒)。 */
  startSec: number;
  /** 発話の終了 (シーン表示秒)。 */
  endSec: number;
}

/** テロップ 1 枚ぶんの対応づけ結果。 */
export interface CaptionWordTimings {
  /** 対応づけを作ったときの text のハッシュ (captionTextHash)。 */
  textHash: string;
  units: CaptionWordTimingUnit[];
}

/** ハイライトの見た目 (テロップ単位。CaptionDesign.wordHighlight)。 */
export interface CaptionWordHighlight {
  /** ハイライト色 (#RRGGBB)。省略 = CAPTION_WORD_HIGHLIGHT_DEFAULT_COLOR。 */
  color?: string;
  /** 色だけ変える (既定) / 色を変えて太くもする。 */
  mode?: CaptionWordHighlightMode;
  /** 発話より少し早く点灯させる秒数 (0〜0.3)。省略 = CAPTION_WORD_HIGHLIGHT_DEFAULT_LEAD_SEC。 */
  leadSec?: number;
}

export const CAPTION_WORD_HIGHLIGHT_MODES = ['color', 'color-bold'] as const;
export type CaptionWordHighlightMode = (typeof CAPTION_WORD_HIGHLIGHT_MODES)[number];

/** 解決済みのハイライト設定 (描画側はこれだけを見る)。 */
export interface ResolvedCaptionWordHighlight {
  color: string;
  mode: CaptionWordHighlightMode;
  leadSec: number;
}

/** ハイライト色の既定。部分強調と同じ黄色系に揃える (1 本の動画で色が散らからないように)。 */
export const CAPTION_WORD_HIGHLIGHT_DEFAULT_COLOR: string = CAPTION_EMPHASIS_DEFAULT_COLOR;

/** mode の既定 (色だけ変える)。 */
export const CAPTION_WORD_HIGHLIGHT_DEFAULT_MODE: CaptionWordHighlightMode = 'color';

/** 点灯の先行秒の既定・上限 (少し早めに点くほうが歌詞字幕らしく見える)。 */
export const CAPTION_WORD_HIGHLIGHT_DEFAULT_LEAD_SEC = 0.05;
/** 字幕が現れた直後は全文を基準色で見せ、その後に歌声の時刻へ追いつく。 */
export const CAPTION_WORD_HIGHLIGHT_INITIAL_HOLD_SEC = 0.12;
/** 二重縁取りの歌詞が詰まりすぎないよう、文字サイズに比例して字間を広げる。 */
export const CAPTION_WORD_HIGHLIGHT_LETTER_SPACING_EM = 0.08;
export const CAPTION_WORD_HIGHLIGHT_LEAD_SEC_MAX = 0.3;
export const CAPTION_WORD_HIGHLIGHT_LEAD_SEC_STEP = 0.01;

/** `color-bold` のときに足すウェイト (部分強調と同じ式で実在するウェイトへ丸める)。 */
export { CAPTION_EMPHASIS_BOLD_WEIGHT_DELTA as CAPTION_WORD_HIGHLIGHT_BOLD_WEIGHT_DELTA } from './captionEmphasis';

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** half-up の丸め (captionEmphasis の roundToStep と同じ式)。 */
function roundToStep(value: number, step: number): number {
  const inv = 1 / step;
  return Math.floor(value * inv + 0.5) / inv;
}

/** FNV-1a (32bit) の 1 バイトぶんの混ぜ込み。 */
function fnv1aMix(hash: number, byte: number): number {
  return Math.imul(hash ^ byte, 0x01000193) >>> 0;
}

/**
 * `text` の安定ハッシュ (FNV-1a 32bit の 16 進 8 桁)。
 *
 * NFC 正規化した文字列を **UTF-8 バイト列**として混ぜるので、Python の
 * `unicodedata.normalize('NFC', text).encode('utf-8')` と同じ並びになり、FE / レンダラー / BE で
 * 同じ値になる (crypto / TextEncoder に依存しない)。
 */
export function captionTextHash(text: string): string {
  const normalized = (typeof text === 'string' ? text : '').normalize('NFC');
  let hash = 0x811c9dc5;
  for (const ch of normalized) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp < 0x80) {
      hash = fnv1aMix(hash, cp);
    } else if (cp < 0x800) {
      hash = fnv1aMix(hash, 0xc0 | (cp >> 6));
      hash = fnv1aMix(hash, 0x80 | (cp & 0x3f));
    } else if (cp < 0x10000) {
      hash = fnv1aMix(hash, 0xe0 | (cp >> 12));
      hash = fnv1aMix(hash, 0x80 | ((cp >> 6) & 0x3f));
      hash = fnv1aMix(hash, 0x80 | (cp & 0x3f));
    } else {
      hash = fnv1aMix(hash, 0xf0 | (cp >> 18));
      hash = fnv1aMix(hash, 0x80 | ((cp >> 12) & 0x3f));
      hash = fnv1aMix(hash, 0x80 | ((cp >> 6) & 0x3f));
      hash = fnv1aMix(hash, 0x80 | (cp & 0x3f));
    }
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * `wordTimings` を保存形へ正規化する。
 *
 * - `textHash` が `text` のハッシュと**一致しないものは丸ごと落とす** (保存値 = 実効値。
 *   text を差し替えた後の古い対応づけを保存し続けない)
 * - unit は数値・整数・text 長の内側・`start < end` のものだけを採り、`start` 昇順に並べ、
 *   **重なる unit は落とす** (前の unit の end より前から始まるもの)
 * - 秒は 0 以上・`startSec <= endSec` に整え、小数 2 桁に丸める
 * - 残る unit が 0 件なら `undefined` (キーごと落とす)
 */
export function normalizeWordTimings(value: unknown, text: string): CaptionWordTimings | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as CaptionWordTimings;
  if (typeof raw.textHash !== 'string' || raw.textHash !== captionTextHash(text)) return undefined;
  if (!Array.isArray(raw.units)) return undefined;
  const textLength = Array.from(typeof text === 'string' ? text : '').length;
  if (textLength <= 0) return undefined;

  const cleaned: CaptionWordTimingUnit[] = [];
  for (const entry of raw.units) {
    if (!entry || typeof entry !== 'object') continue;
    const unit = entry as CaptionWordTimingUnit;
    if (!isFiniteNumber(unit.start) || !isFiniteNumber(unit.end)) continue;
    if (!isFiniteNumber(unit.startSec) || !isFiniteNumber(unit.endSec)) continue;
    const start = clamp(Math.floor(unit.start), 0, textLength);
    const end = clamp(Math.floor(unit.end), 0, textLength);
    if (start >= end) continue;
    const startSec = roundToStep(Math.max(0, unit.startSec), 0.01);
    const endSec = roundToStep(Math.max(startSec, unit.endSec), 0.01);
    cleaned.push({ start, end, startSec, endSec });
  }
  if (cleaned.length === 0) return undefined;
  cleaned.sort((a, b) => a.start - b.start || a.end - b.end);
  const units: CaptionWordTimingUnit[] = [];
  for (const unit of cleaned) {
    const last = units[units.length - 1];
    // 重なりは落とす (前の unit の end と同じ位置から始まる「隣接」は許す)
    if (last && unit.start < last.end) continue;
    units.push(unit);
  }
  return { textHash: raw.textHash, units };
}

/** 有効な対応づけを持つか (要約・書き出しの注記で使う)。 */
export function hasCaptionWordTimings(
  caption: { text?: string; wordTimings?: CaptionWordTimings | null } | null | undefined,
): boolean {
  if (!caption) return false;
  return (normalizeWordTimings(caption.wordTimings, caption.text ?? '')?.units.length ?? 0) > 0;
}

/**
 * `CaptionDesign.wordHighlight` の外部入力 (AI の提案・古い保存データ) を保存形へ正規化する。
 *
 * **キーの存在自体が「単語ハイライトを使う」宣言**なので、既定値と同じ項目を落としても
 * `{}` (空オブジェクト) は残す。オブジェクトでない値 (null・false・文字列) は `undefined`。
 */
export function normalizeCaptionWordHighlight(value: unknown): CaptionWordHighlight | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as CaptionWordHighlight;
  const result: CaptionWordHighlight = {};
  if (typeof raw.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.color)) {
    result.color = raw.color.toUpperCase();
  }
  if (
    typeof raw.mode === 'string'
    && (CAPTION_WORD_HIGHLIGHT_MODES as readonly string[]).includes(raw.mode)
    && raw.mode !== CAPTION_WORD_HIGHLIGHT_DEFAULT_MODE
  ) {
    result.mode = raw.mode;
  }
  if (isFiniteNumber(raw.leadSec)) {
    const lead = roundToStep(
      clamp(raw.leadSec, 0, CAPTION_WORD_HIGHLIGHT_LEAD_SEC_MAX),
      CAPTION_WORD_HIGHLIGHT_LEAD_SEC_STEP,
    );
    if (Math.abs(lead - CAPTION_WORD_HIGHLIGHT_DEFAULT_LEAD_SEC) > 1e-9) result.leadSec = lead;
  }
  return result;
}

/** 単語ハイライトが有効な design か (キーの存在で決まる)。 */
export function hasCaptionWordHighlight(
  design: { wordHighlight?: CaptionWordHighlight | null } | null | undefined,
): boolean {
  return normalizeCaptionWordHighlight(design?.wordHighlight) !== undefined;
}

/** ハイライトの実効値 (描画側はこれだけを見る)。無効な design でも既定値を返す。 */
export function resolveCaptionWordHighlight(
  design: { wordHighlight?: CaptionWordHighlight | null } | null | undefined,
): ResolvedCaptionWordHighlight {
  const highlight = normalizeCaptionWordHighlight(design?.wordHighlight);
  return {
    color: highlight?.color ?? CAPTION_WORD_HIGHLIGHT_DEFAULT_COLOR,
    mode: highlight?.mode ?? CAPTION_WORD_HIGHLIGHT_DEFAULT_MODE,
    leadSec: highlight?.leadSec ?? CAPTION_WORD_HIGHLIGHT_DEFAULT_LEAD_SEC,
  };
}

/**
 * シーン表示秒 `tSec` の時点でハイライトが掛かる範囲 (先頭から k 文字目まで)。
 *
 * `startSec - leadSec <= tSec` を満たす**最後の unit の end** までを返す (発話済み + 発話中)。
 * 対応づけが無い / `textHash` が `text` と合わない / まだ 1 語も始まっていないときは `null`
 * (= ハイライト無しの通常表示。文字数按分による疑似ハイライトはしない。§4.2-B2)。
 */
export function wordHighlightRangeAt(
  wordTimings: CaptionWordTimings | null | undefined,
  text: string,
  tSec: number,
  leadSec: number = CAPTION_WORD_HIGHLIGHT_DEFAULT_LEAD_SEC,
  captionStartSec?: number,
): CaptionEmphasisRange | null {
  const normalized = normalizeWordTimings(wordTimings, text);
  if (!normalized || normalized.units.length === 0) return null;
  if (!isFiniteNumber(tSec)) return null;
  if (isFiniteNumber(captionStartSec) && tSec < captionStartSec + CAPTION_WORD_HIGHLIGHT_INITIAL_HOLD_SEC) {
    return null;
  }
  const lead = isFiniteNumber(leadSec)
    ? clamp(leadSec, 0, CAPTION_WORD_HIGHLIGHT_LEAD_SEC_MAX)
    : CAPTION_WORD_HIGHLIGHT_DEFAULT_LEAD_SEC;
  let end = 0;
  for (const unit of normalized.units) {
    if (unit.startSec - lead > tSec) break;
    end = unit.end;
  }
  if (end <= 0) return null;
  return { start: 0, end };
}

/** UI / 要約の表示名 (「単語ハイライト #FFE14D 太字」)。 */
export function captionWordHighlightLabel(
  design: { wordHighlight?: CaptionWordHighlight | null } | null | undefined,
): string {
  const style = resolveCaptionWordHighlight(design);
  const parts = [style.color];
  if (style.mode === 'color-bold') parts.push('太字');
  if (Math.abs(style.leadSec - CAPTION_WORD_HIGHLIGHT_DEFAULT_LEAD_SEC) > 1e-9) {
    parts.push(`先行 ${style.leadSec}s`);
  }
  return parts.join(' ');
}
